import { useMutation, useQuery, useQueryClient } from 'react-query';

import { http, type JsonData, underscoreToCamelCase } from '@/api/base/http';
import type { CreativeDetails, CreativeListResponse, CreativeQuery } from '@/api/types/creative';

export const useCreativeQuery = (id: number, args: Record<string, unknown> = {}) => {
    return useQuery<CreativeDetails, Error>({
        queryKey: ['creatives', id],
        queryFn: () => http.get<JsonData>(`/creatives/${id}`).then((d) => underscoreToCamelCase(d)),
        ...args,
    });
};

export const useCreativeListQuery = (query?: CreativeQuery, args: Record<string, unknown> = {}) => {
    return useQuery<CreativeListResponse, Error>({
        queryKey: ['creatives', query],
        queryFn: () =>
            http.get<JsonData>('/creatives', query as Record<string, unknown>).then((d) => underscoreToCamelCase(d)),
        ...args,
    });
};

export const useDeleteCreative = (args: Record<string, unknown> = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => http.delete<void>(`/creatives/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries('creatives');
        },
        ...args,
    });
};

export const useDuplicateCreative = (args: Record<string, unknown> = {}) => {
    const queryClient = useQueryClient();
    if ('onSuccess' in args) {
        const onSuccess = args.onSuccess as () => void;
        args.onSuccess = () => {
            queryClient.invalidateQueries('creatives');
            onSuccess();
        };
    } else {
        args.onSuccess = () => {
            queryClient.invalidateQueries('creatives');
        };
    }
    return useMutation({
        mutationFn: (id: number) => http.post<JsonData>(`/creatives/${id}/duplicate`, {}).then(underscoreToCamelCase),
        ...args,
    });
};

export const useReconcileCreative = (args: Record<string, unknown> = {}) => {
    const queryClient = useQueryClient();
    if ('onSuccess' in args) {
        const onSuccess = args.onSuccess as () => void;
        args.onSuccess = (data: { id: number }) => {
            queryClient.invalidateQueries(['creatives', data.id]);
            onSuccess();
        };
    } else {
        args.onSuccess = (data: { id: number }) => {
            queryClient.invalidateQueries(['creatives', data.id]);
        };
    }
    return useMutation({
        mutationFn: (id: number) =>
            http
                .patch<JsonData>(`/creatives/${id}/reconcile`, {})
                .then(underscoreToCamelCase)
                .then((_) => ({ id })),
        ...args,
    });
};
